import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { apiStatus } from 'shared/constants/AppEnum'
import { IApiState } from 'store/types'
import {
    StaffProfileResponseType,
    StaffTimeOffPayloadType,
    StaffTimeOffResponseType,
} from 'types/api/temple'
import { extractError, isFieldError } from 'utils/api'
import * as yup from 'yup'

interface FormStaffTimeOffProps {
    initialValues?: StaffTimeOffResponseType
    profiles: StaffProfileResponseType[]
    buttonLabel: string
    api: IApiState
    onSubmit: (values: StaffTimeOffPayloadType, helpers: FormikHelpers<StaffTimeOffPayloadType>) => void | Promise<any>
}

const initialFormValues = (item?: StaffTimeOffResponseType): StaffTimeOffPayloadType => ({
    staffProfile: item?.staffProfile ?? '',
    mode: item?.mode ?? 'time_off',
    leaveType: item?.leaveType ?? 'leave',
    sessionType: item?.sessionType ?? 'full_day',
    startDate: item?.startDate ?? '',
    endDate: item?.endDate ?? '',
    startTime: item?.startTime ?? '',
    endTime: item?.endTime ?? '',
    recurrence: item?.recurrence ?? 'none',
    recurrenceInterval: item?.recurrenceInterval ?? 1,
    reason: item?.reason ?? '',
    isActive: item?.isActive ?? true,
})

const normalizePayload = (values: StaffTimeOffPayloadType): StaffTimeOffPayloadType => ({
    ...values,
    recurrence: values.mode === 'recurrence' ? values.recurrence : 'none',
    recurrenceInterval: Number(values.recurrenceInterval || 1),
    startTime: values.sessionType === 'time_based' ? values.startTime : null,
    endTime: values.sessionType === 'time_based' ? values.endTime : null,
})

const getConflictText = (error: any) => {
    if (!error || typeof error !== 'object' || !Array.isArray(error.conflicts)) return ''
    return error.conflicts
        .map((conflict: any) => `${conflict.date} ${conflict.startTime}-${conflict.endTime}: ${conflict.serviceName}`)
        .join('; ')
}

const FormStaffTimeOff: React.FC<FormStaffTimeOffProps> = ({
    initialValues,
    profiles,
    buttonLabel,
    api,
    onSubmit,
}) => {
    const validationSchema = yup.object({
        staffProfile: yup.string().required('Staff/Priest is required'),
        startDate: yup.string().required('Start date is required'),
        endDate: yup.string().required('End date is required'),
        recurrenceInterval: yup.number().min(1, 'Must be at least 1').required('Required'),
        startTime: yup.string().when('sessionType', {
            is: 'time_based',
            then: yup.string().required('Start time is required'),
        }),
        endTime: yup.string().when('sessionType', {
            is: 'time_based',
            then: yup.string().required('End time is required'),
        }),
    })

    const conflictText = getConflictText(api.error)

    return (
        <Formik
            enableReinitialize
            initialValues={initialFormValues(initialValues)}
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => onSubmit(normalizePayload(values), helpers)}
        >
            {({ dirty, errors, isSubmitting, isValid, setFieldValue, touched, values }) => (
                <Form noValidate autoComplete='off'>
                    <Grid container spacing={6} direction='column'>
                        {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                            <Grid item>
                                <Alert severity='error'>
                                    {conflictText || extractError(api.error)}
                                </Alert>
                            </Grid>
                        )}
                        <Grid item>
                            <FormControl variant='outlined' fullWidth required error={Boolean(touched.staffProfile && errors.staffProfile)}>
                                <InputLabel id='staff-time-off-profile-label'>Staff/Priest</InputLabel>
                                <Select
                                    labelId='staff-time-off-profile-label'
                                    label='Staff/Priest'
                                    value={values.staffProfile}
                                    onChange={event => setFieldValue('staffProfile', event.target.value)}
                                >
                                    {profiles.map(profile => (
                                        <MenuItem key={profile.id} value={profile.id}>
                                            {profile.fullName} ({profile.roleType})
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.staffProfile && errors.staffProfile && <FormHelperText>{errors.staffProfile}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth>
                                    <InputLabel id='staff-time-off-mode-label'>Mode</InputLabel>
                                    <Select
                                        labelId='staff-time-off-mode-label'
                                        label='Mode'
                                        value={values.mode}
                                        onChange={event => {
                                            setFieldValue('mode', event.target.value)
                                            if (event.target.value === 'time_off') setFieldValue('recurrence', 'none')
                                        }}
                                    >
                                        <MenuItem value='time_off'>Time Off</MenuItem>
                                        <MenuItem value='recurrence'>Recurrence</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth>
                                    <InputLabel id='staff-leave-type-label'>Leave Type</InputLabel>
                                    <Select
                                        labelId='staff-leave-type-label'
                                        label='Leave Type'
                                        value={values.leaveType}
                                        onChange={event => {
                                            setFieldValue('leaveType', event.target.value)
                                            if (event.target.value === 'permission') setFieldValue('sessionType', 'time_based')
                                        }}
                                    >
                                        <MenuItem value='leave'>Leave</MenuItem>
                                        <MenuItem value='permission'>Permission</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='startDate' label='Start Date' type='date' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='endDate' label='End Date' type='date' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={4}>
                                <FormControl variant='outlined' fullWidth>
                                    <InputLabel id='staff-session-type-label'>Session</InputLabel>
                                    <Select
                                        labelId='staff-session-type-label'
                                        label='Session'
                                        value={values.sessionType}
                                        onChange={event => setFieldValue('sessionType', event.target.value)}
                                    >
                                        <MenuItem value='full_day'>Full Day</MenuItem>
                                        <MenuItem value='fn'>FN</MenuItem>
                                        <MenuItem value='an'>AN</MenuItem>
                                        <MenuItem value='time_based'>Time Based</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormikTextField
                                    name='startTime'
                                    label='Start Time'
                                    type='time'
                                    variant='outlined'
                                    disabled={values.sessionType !== 'time_based'}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormikTextField
                                    name='endTime'
                                    label='End Time'
                                    type='time'
                                    variant='outlined'
                                    disabled={values.sessionType !== 'time_based'}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth disabled={values.mode !== 'recurrence'}>
                                    <InputLabel id='staff-recurrence-label'>Recurrence</InputLabel>
                                    <Select
                                        labelId='staff-recurrence-label'
                                        label='Recurrence'
                                        value={values.recurrence}
                                        onChange={event => setFieldValue('recurrence', event.target.value)}
                                    >
                                        <MenuItem value='none'>None</MenuItem>
                                        <MenuItem value='daily'>Daily</MenuItem>
                                        <MenuItem value='weekly'>Weekly</MenuItem>
                                        <MenuItem value='monthly'>Monthly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormikTextField
                                    name='recurrenceInterval'
                                    label='Recurrence Interval'
                                    type='number'
                                    variant='outlined'
                                    disabled={values.mode !== 'recurrence'}
                                />
                            </Grid>
                        </Grid>
                        <Grid item>
                            <FormikTextField
                                name='reason'
                                label='Reason'
                                placeholder='Leave reason or note'
                                variant='outlined'
                                multiline
                                rows={4}
                            />
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color='primary'
                                        checked={Boolean(values.isActive)}
                                        onChange={event => setFieldValue('isActive', event.target.checked)}
                                    />
                                }
                                label='Active'
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                color='primary'
                                variant='contained'
                                type='submit'
                                disabled={isSubmitting || !dirty || !isValid || api.status === apiStatus.LOADING}
                            >
                                {buttonLabel}
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export default FormStaffTimeOff
